import useAppDispatch from "../../../../../../hooks/redux/base/useAppDispatch";
import {
  AsyncCallArgs,
  BlockText,
  CircularLoader,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  isNotEmpty,
  RichTextFormField,
  useAsyncCall,
  useReactHookForm,
  ViewDialog,
} from "@duosoftbg/nacid-components";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import useAppSelector from "../../../../../../hooks/redux/base/useAppSelector";
import { FormProvider } from "react-hook-form";
import { ServiceDefinitionSaveDetails } from "../../../../../../types/serviceDefinitionTypes";
import { serviceDefinitionSaveInitialValues } from "../../../../../../init/serviceDefinitionSaveInitialValues";
import { createServiceDefinitionSaveValidationSchema } from "../../../../../../yup/schema/serviceDefinition/serviceDefinitionSaveValidationSchema";
import { getContentManagementDataById, updateServiceDefinition } from "../../../../../../axios/api/services";
import { toast } from "react-toastify";
import { closeModal } from "../../../../../../store/redux/slice/ComponentsControl/serviceDefinitionControl";
import { Alert } from "@mui/material";

const Content = ({ loading, error, methods, id }) => {
  const { t } = useTranslation();

  if (loading) {
    return <CircularLoader />;
  }

  if (error) {
    return <Alert severity="error">{t("m.error.serverFetchingError")}</Alert>;
  }
  return (
    <>
      {!id && <CircularLoader />}
      {id && (
        <FormProvider {...methods}>
          <form>
            <GridContainer spacing={5}>
              <GridItem sm={12} md={12}>
                <BlockText label={"l.serviceDefinition.id"} text={t(`m.${id}`)} />
              </GridItem>
              <GridItem sm={12} md={12}>
                <RichTextFormField
                  fieldName={"content"}
                  onChangeMethod={(content) => methods.setValue("content", content)}
                />
              </GridItem>
            </GridContainer>
          </form>
        </FormProvider>
      )}
    </>
  );
};

const ServiceDefinitionEditDialog = () => {
  const dispatch = useAppDispatch();
  const { asyncCall } = useAsyncCall();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.serviceDefinitionControl.modals.edit;
  });

  const { methods } = useReactHookForm<ServiceDefinitionSaveDetails>({
    defaultValues: serviceDefinitionSaveInitialValues,
    validationSchema: createServiceDefinitionSaveValidationSchema,
  });

  const handleCloseDialog = () => {
    methods.reset(serviceDefinitionSaveInitialValues);
    dispatch(closeModal({ modalType: "edit" }));
  };

  useEffect(() => {
    if (open) {
      if (isNotEmpty(id)) {
        const asyncCallArgs: AsyncCallArgs = {
          promise: getContentManagementDataById(id),
          onSuccess: (response) => {
            methods.setValue("id", id);
            methods.setValue("content", response.content);
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      } else {
        methods.reset(serviceDefinitionSaveInitialValues);
        setLoading(false);
      }
    }
  }, [asyncCall, id, methods, open]);

  const onSubmit = (values) => {
    const asyncCreation: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: updateServiceDefinition(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        methods.reset(serviceDefinitionSaveInitialValues, {
          keepIsSubmitted: true,
          keepSubmitCount: true,
        });
        toast.success(t("m.update.serviceDefinition.success"));
        handleCloseDialog();
      },
    };
    asyncCall(asyncCreation);
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={"t.service.definition.edit.dialog"}
      disableEnforceFocus
      dialogActionsSpacing={{ pr: 3 }}
      onSubmitBtnClick={methods.handleSubmit(onSubmit, () =>
        handleSubmitValidationFail(methods.formState, t, undefined),
      )}
    >
      <Content loading={loading} error={error} methods={methods} id={id}></Content>
    </ViewDialog>
  );
};

export default ServiceDefinitionEditDialog;
